import React, { useEffect, useState, useMemo, useContext } from "react";
import { useParams } from "react-router-dom";
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Stack,
  Chip,
  Skeleton,
  useMediaQuery,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTheme, alpha } from "@mui/material/styles";
import AutoGraphOutlinedIcon from "@mui/icons-material/AutoGraphOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import TrendingDownOutlinedIcon from "@mui/icons-material/TrendingDownOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import GridViewOutlinedIcon from "@mui/icons-material/GridViewOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import PygAnaliticoPageHeader, {
  pygToolbarFieldSx,
  pygToolbarControlSx,
  pygToolbarSidebarFullRowSx,
} from "./PygAnaliticoPageHeader";
import {
  PygToolbarPanel,
  PygToolbarFilters,
  PygExportMenu,
} from "./PygAnaliticoToolbar";
import { jwtDecode } from "jwt-decode";
import { useTranslation } from "react-i18next";
import { SidebarContext } from "../Sidebar/SidebarProvider";
import * as XLSX from "xlsx";
import {
  MESES as meses,
  buildPygAnaliticoStructure,
  buildExcelRows,
  calcularProyeccionPorMes,
  PYG_TITULO_VENTA_NPGC,
  formatearPctSobreVenta,
} from "./pygAnaliticoBuild";
import { exportPygPorMarcas, exportPygPorEmpresas } from "./pygAnaliticoExport";
import { fetchPygChartSeriesByYears } from "./pygAnaliticoFetch";
import PygAnaliticoPipelineVentaChart from "./PygAnaliticoPipelineVentaChart";
import PygAnaliticoYearCompareChart from "./PygAnaliticoYearCompareChart";
import PygCompareYearsControl from "./PygCompareYearsControl";
import PygSerieDetalleDialog from "./PygSerieDetalleDialog";
import { hasPygSerieDetalle } from "./pygSerieDetalleConfig";
import { DEFAULT_PYG_COMPARE_YEARS } from "./pygChartCompareYears";
import {
  PYG_VISTA_OPTIONS,
  DEFAULT_PYG_VISTA,
  normalizePygVista,
  resolvePygVista,
  getPygVistaApiParams,
  getPygFiltroResumen,
  pygAnaliticoBasePath,
  isNmlSpainPygVista,
  canAccessPygVista,
  pygVistaFallbackPath,
  NML_BALANCE_PATH,
} from "../../constants/pygEmpresas";
import { getUserById } from "../../api/User/userApi";
import { mapUserToPermissionState } from "../../utils/userPermissionsUtils";
import {
  translatePygLine,
  pygVistaLabelKey,
  pygFiltroResumenKey,
  pygExcelI18nOpts,
} from "../../constants/pygLineI18n";
import { pageContentCardSx } from "../../styles/pageModernStyles";
import { authFetch } from "../../api/authFetch";

const API_URL = process.env.REACT_APP_API_URL;

const COLOR_VALOR_POSITIVO = "#2E7D32";
const COLOR_VALOR_NEGATIVO = "#FF2800";
const COLOR_VALOR_CERO = "rgba(92, 114, 117, 0.55)";

const colorPorSigno = (valor) => {
  if (valor === 0 || valor === null || valor === undefined)
    return COLOR_VALOR_CERO;
  return Number(valor) >= 0 ? COLOR_VALOR_POSITIVO : COLOR_VALOR_NEGATIVO;
};

const getEstiloValor = (valor) => ({
  color: colorPorSigno(valor),
  fontWeight: 500,
  fontSize: "12px",
});

const formatearNumero = (valor) =>
  (valor ?? 0).toLocaleString("es-ES", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: true,
  });

const CeldaTotalConPctVenta = ({
  total,
  ventaReferencia,
  sx = {},
  fontWeight = "bold",
  onClick,
}) => {
  const valor = Number(total) || 0;
  const pctLabel = formatearPctSobreVenta(valor, ventaReferencia);
  const color = colorPorSigno(valor);

  return (
    <TableCell
      align="right"
      onClick={onClick}
      sx={{
        fontWeight,
        color,
        borderLeft: sx.borderLeft,
        ...sx,
      }}
    >
      {formatearNumero(valor)}
      {pctLabel ? (
        <Typography
          component="span"
          variant="caption"
          display="block"
          sx={{
            color,
            fontSize: "10px",
            fontWeight: 500,
            lineHeight: 1.15,
            mt: 0.2,
            opacity: 0.92,
          }}
        >
          {pctLabel}
        </Typography>
      ) : null}
    </TableCell>
  );
};

const PygAnaliticoTable = ({
  pygVista: pygVistaProp,
  nivel1,
  nivel2,
  hideYearSelector,
  year: yearProp,
  embedded,
  compactEmbedded,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const { contentMargin } = useContext(SidebarContext);
  const { empresa: empresaParam } = useParams();
  const activeVista = pygVistaProp ?? normalizePygVista(empresaParam);
  const vistaConfig = resolvePygVista(activeVista);
  const {
    empresa: empresaApi,
    nivel1: nivel1Api,
    omitirGeneralStage,
  } = getPygVistaApiParams(activeVista);
  const pageShell = !embedded && !compactEmbedded;
  const accent = theme.palette.primary.main;
  const pageGradient =
    theme.palette.mode === "dark"
      ? `linear-gradient(180deg, ${theme.palette.background.default} 0%, ${alpha(theme.palette.primary.main, 0.08)} 100%)`
      : "linear-gradient(180deg, #f8fafc 0%, #eef6f8 45%, #f5f3ff 100%)";
  const filtroResumen = t(pygFiltroResumenKey(activeVista), {
    defaultValue: getPygFiltroResumen(activeVista),
  });
  const lockVista = isNmlSpainPygVista(activeVista);

  const appendPygFilters = (url) => {
    let next = url;
    if (nivel1Api) next += `&nivel1=${encodeURIComponent(nivel1Api)}`;
    if (omitirGeneralStage) next += "&omitirGeneralStage=1";
    return next;
  };

  const [yearState, setYearState] = useState(new Date().getFullYear());
  const year = yearProp != null ? yearProp : yearState;
  const [rawData, setRawData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [proyeccionVentas, setProyeccionVentas] = useState([]);
  const [proyeccionCompras, setProyeccionCompras] = useState([]);
  const [pedidosVenta, setPedidosVenta] = useState([]);
  const [pedidosCompra, setPedidosCompra] = useState([]);
  const [facturasBorradorVenta, setFacturasBorradorVenta] = useState([]);
  const [marcas, setMarcas] = useState([]);
  const [selectedMarca, setSelectedMarca] = useState("Todas");
  const [loadingMarcas, setLoadingMarcas] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportStatus, setExportStatus] = useState("");
  const [compareYears, setCompareYears] = useState(() => [
    ...DEFAULT_PYG_COMPARE_YEARS,
  ]);
  const [ventaByYear, setVentaByYear] = useState(null);
  const [ventaContableByYear, setVentaContableByYear] = useState(null);
  const [loadingVentaChart, setLoadingVentaChart] = useState(true);
  const [errorVentaChart, setErrorVentaChart] = useState(null);
  const [pipelineBreakdownByYear, setPipelineBreakdownByYear] = useState(null);
  const [loadingPipelineChart, setLoadingPipelineChart] = useState(true);
  const [errorPipelineChart, setErrorPipelineChart] = useState(null);
  const [resultadoFinalByYear, setResultadoFinalByYear] = useState(null);
  const [loadingResultadoChart, setLoadingResultadoChart] = useState(true);
  const [errorResultadoChart, setErrorResultadoChart] = useState(null);
  const [detalleDialog, setDetalleDialog] = useState(null);

  const openSerieDetalle = (titulo, mes = null, totalEsperado = null) => {
    if (!hasPygSerieDetalle(titulo)) return;
    setDetalleDialog({ titulo, mes, totalEsperado });
  };

  const closeSerieDetalle = () => setDetalleDialog(null);

  const onVistaChange = (nextVista) => {
    navigate(pygAnaliticoBasePath(nextVista, false));
  };

  useEffect(() => {
    if (!empresaParam) {
      navigate(pygAnaliticoBasePath(DEFAULT_PYG_VISTA, false), {
        replace: true,
      });
    }
  }, [empresaParam, navigate]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return navigate("/login");
    let cancelled = false;
    (async () => {
      try {
        const decoded = jwtDecode(token);
        if (decoded.exp < Date.now() / 1000) {
          localStorage.removeItem("token");
          navigate("/login");
          return;
        }
        const user = await getUserById(decoded.userId);
        if (cancelled) return;
        const perms = mapUserToPermissionState(user);
        if (!canAccessPygVista(perms, activeVista)) {
          navigate(pygVistaFallbackPath(perms, false), { replace: true });
        }
      } catch {
        if (!cancelled) navigate("/login");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate, activeVista]);

  useEffect(() => {
    if (nivel2) return; // En modo embebido por tarjeta ya viene fijada la marca.
    setLoadingMarcas(true);
    const fetchMarcas = async () => {
      let url = `${API_URL}/api/pyg/marcas-npgc?year=${year}&empresa=${encodeURIComponent(empresaApi)}`;
      url = appendPygFilters(url);

      const res = await authFetch(url, { cache: "no-store" });
      if (res.ok) {
        const data = await res.json();
        const lista = Array.isArray(data?.marcas) ? data.marcas : [];
        return lista
          .map((m) => String(m || "").trim())
          .filter(Boolean)
          .sort((a, b) => a.localeCompare(b));
      }
      if (res.status !== 404) throw new Error(res.statusText);

      // Fallback por si el endpoint de marcas no existe en algún entorno.
      let fallbackUrl = `${API_URL}/api/pyg/saldos-npgc?year=${year}&empresa=${encodeURIComponent(empresaApi)}`;
      fallbackUrl = appendPygFilters(fallbackUrl);
      const fallbackRes = await authFetch(fallbackUrl, { cache: "no-store" });
      if (!fallbackRes.ok) throw new Error(fallbackRes.statusText);
      const rows = await fallbackRes.json();
      const uniq = new Set();
      (Array.isArray(rows) ? rows : []).forEach((r) => {
        const marca = String(
          r?.Nivel2Analitica ?? r?.nivel2Analitica ?? "",
        ).trim();
        if (marca) uniq.add(marca);
      });
      return Array.from(uniq).sort((a, b) => a.localeCompare(b));
    };

    fetchMarcas()
      .then((lista) => setMarcas(lista))
      .catch(() => setMarcas([]))
      .finally(() => setLoadingMarcas(false));
  }, [year, empresaApi, nivel1Api, omitirGeneralStage, nivel2, activeVista]);

  useEffect(() => {
    if (nivel2) return;
    if (selectedMarca === "Todas") return;
    if (!marcas.includes(selectedMarca)) {
      setSelectedMarca("Todas");
    }
  }, [marcas, selectedMarca, nivel2]);

  const nivel2Filter =
    nivel2 || (selectedMarca !== "Todas" ? selectedMarca : undefined);

  useEffect(() => {
    setLoading(true);
    setError(null);
    let url = `${API_URL}/api/pyg/saldos-npgc?year=${year}&empresa=${encodeURIComponent(empresaApi)}`;
    url = appendPygFilters(url);
    if (nivel2Filter) url += `&nivel2=${encodeURIComponent(nivel2Filter)}`;
    authFetch(url, { cache: "no-store" })
      .then((res) => {
        if (!res.ok) throw new Error(res.statusText);
        return res.json();
      })
      .then(setRawData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [
    year,
    empresaApi,
    nivel1Api,
    omitirGeneralStage,
    nivel2Filter,
    activeVista,
  ]);

  // Datos de proyección y pedidos (mismo origen que en PygDebugTable)
  useEffect(() => {
    let url = `${API_URL}/api/pyg/proyeccion_ventas?year=${year}&empresa=${encodeURIComponent(empresaApi)}`;
    url = appendPygFilters(url);
    if (nivel2Filter) url += `&nivel2=${encodeURIComponent(nivel2Filter)}`;
    authFetch(url)
      .then((res) => res.json())
      .then((data) => setProyeccionVentas(Array.isArray(data) ? data : []))
      .catch(() => setProyeccionVentas([]));
  }, [
    year,
    empresaApi,
    nivel1Api,
    omitirGeneralStage,
    nivel2Filter,
    activeVista,
  ]);

  useEffect(() => {
    let url = `${API_URL}/api/pyg/proyeccion_compras?year=${year}&empresa=${encodeURIComponent(empresaApi)}`;
    url = appendPygFilters(url);
    if (nivel2Filter) url += `&nivel2=${encodeURIComponent(nivel2Filter)}`;
    authFetch(url)
      .then((res) => res.json())
      .then((data) => setProyeccionCompras(Array.isArray(data) ? data : []))
      .catch(() => setProyeccionCompras([]));
  }, [
    year,
    empresaApi,
    nivel1Api,
    omitirGeneralStage,
    nivel2Filter,
    activeVista,
  ]);

  useEffect(() => {
    let url = `${API_URL}/api/pyg/pedidos_venta?year=${year}&empresa=${encodeURIComponent(empresaApi)}`;
    url = appendPygFilters(url);
    if (nivel2Filter) url += `&nivel2=${encodeURIComponent(nivel2Filter)}`;
    authFetch(url)
      .then((res) => res.json())
      .then((data) => setPedidosVenta(Array.isArray(data) ? data : []))
      .catch(() => setPedidosVenta([]));
  }, [
    year,
    empresaApi,
    nivel1Api,
    omitirGeneralStage,
    nivel2Filter,
    activeVista,
  ]);

  useEffect(() => {
    let url = `${API_URL}/api/pyg/pedidos_compra?year=${year}&empresa=${encodeURIComponent(empresaApi)}`;
    url = appendPygFilters(url);
    if (nivel2Filter) url += `&nivel2=${encodeURIComponent(nivel2Filter)}`;
    authFetch(url)
      .then((res) => res.json())
      .then((data) => setPedidosCompra(Array.isArray(data) ? data : []))
      .catch(() => setPedidosCompra([]));
  }, [
    year,
    empresaApi,
    nivel1Api,
    omitirGeneralStage,
    nivel2Filter,
    activeVista,
  ]);

  useEffect(() => {
    let url = `${API_URL}/api/pyg/facturas_venta_borrador?year=${year}&empresa=${encodeURIComponent(empresaApi)}`;
    url = appendPygFilters(url);
    if (nivel2Filter) url += `&nivel2=${encodeURIComponent(nivel2Filter)}`;
    authFetch(url)
      .then((res) => res.json())
      .then((data) => setFacturasBorradorVenta(Array.isArray(data) ? data : []))
      .catch(() => setFacturasBorradorVenta([]));
  }, [
    year,
    empresaApi,
    nivel1Api,
    omitirGeneralStage,
    nivel2Filter,
    activeVista,
  ]);

  const showResultadoFinalChart = !compactEmbedded && !embedded;

  const chartFetchParams = useMemo(
    () => ({
      empresa: empresaApi,
      nivel1: nivel1Api,
      omitirGeneralStage,
      nivel2: nivel2Filter,
    }),
    [empresaApi, nivel1Api, omitirGeneralStage, nivel2Filter],
  );

  useEffect(() => {
    if (!showResultadoFinalChart) return;
    let cancelled = false;
    setLoadingVentaChart(true);
    setLoadingPipelineChart(true);
    setLoadingResultadoChart(true);
    setErrorVentaChart(null);
    setErrorPipelineChart(null);
    setErrorResultadoChart(null);

    fetchPygChartSeriesByYears(API_URL, chartFetchParams, compareYears)
      .then(
        ({
          ventaByYear,
          ventaContableByYear,
          pipelineByYear,
          resultadoByYear,
        }) => {
          if (cancelled) return;
          setVentaByYear(ventaByYear);
          setVentaContableByYear(ventaContableByYear);
          setPipelineBreakdownByYear(pipelineByYear);
          setResultadoFinalByYear(resultadoByYear);
          if (!Object.keys(ventaByYear).length && compareYears.length) {
            const msg = "No se pudieron cargar los datos del gráfico";
            setErrorVentaChart(msg);
            setErrorPipelineChart(msg);
            setErrorResultadoChart(msg);
          }
        },
      )
      .catch((e) => {
        if (cancelled) return;
        const msg = e.message || "Error al cargar gráficos";
        setVentaByYear(null);
        setVentaContableByYear(null);
        setPipelineBreakdownByYear(null);
        setResultadoFinalByYear(null);
        setErrorVentaChart(msg);
        setErrorPipelineChart(msg);
        setErrorResultadoChart(msg);
      })
      .finally(() => {
        if (cancelled) return;
        setLoadingVentaChart(false);
        setLoadingPipelineChart(false);
        setLoadingResultadoChart(false);
      });

    return () => {
      cancelled = true;
    };
  }, [showResultadoFinalChart, chartFetchParams, compareYears, activeVista]);

  const resultadoProyeccionVentas = useMemo(
    () => calcularProyeccionPorMes(proyeccionVentas, year),
    [proyeccionVentas, year],
  );

  const resultadoProyeccionCompras = useMemo(
    () => calcularProyeccionPorMes(proyeccionCompras, year),
    [proyeccionCompras, year],
  );

  const resultadoPedidosVenta = useMemo(
    () => calcularProyeccionPorMes(pedidosVenta, year),
    [pedidosVenta, year],
  );

  const resultadoPedidosCompra = useMemo(
    () => calcularProyeccionPorMes(pedidosCompra, year),
    [pedidosCompra, year],
  );

  const resultadoFacturasBorradorVenta = useMemo(
    () => calcularProyeccionPorMes(facturasBorradorVenta, year),
    [facturasBorradorVenta, year],
  );

  const { groupOrder, groupMap } = useMemo(
    () =>
      buildPygAnaliticoStructure(rawData, {
        resultadoProyeccionVentas,
        resultadoProyeccionCompras,
        resultadoPedidosVenta,
        resultadoPedidosCompra,
        resultadoFacturasBorradorVenta,
      }),
    [
      rawData,
      resultadoProyeccionVentas,
      resultadoProyeccionCompras,
      resultadoPedidosVenta,
      resultadoPedidosCompra,
      resultadoFacturasBorradorVenta,
    ],
  );

  const [gruposAbiertos, setGruposAbiertos] = useState({});
  const toggleGrupo = (titulo) => {
    setGruposAbiertos((prev) => ({ ...prev, [titulo]: !prev[titulo] }));
  };

  const nivel1Param = nivel1Api || undefined;

  const exportToExcel = () => {
    const { headers, rows } = buildExcelRows(
      groupOrder,
      groupMap,
      meses,
      pygExcelI18nOpts(t),
    );
    const ws = XLSX.utils.json_to_sheet(rows, { header: headers });
    const wb = XLSX.utils.book_new();
    const suffix = nivel2Filter ? `-${nivel2Filter}` : "";
    XLSX.utils.book_append_sheet(wb, ws, "PyG Analítico");
    const fileVista = activeVista || "stage";
    XLSX.writeFile(wb, `pyg-analitico-${fileVista}${suffix}-${year}.xlsx`);
  };

  const handleExportPorMarcas = async () => {
    setExporting(true);
    setExportStatus(t("pygAnalitico.exportPreparingBrand"));
    try {
      const count = await exportPygPorMarcas({
        apiUrl: API_URL,
        empresa: empresaApi,
        year,
        nivel1: nivel1Param,
        omitirGeneralStage,
        marcas,
        t,
        onProgress: setExportStatus,
      });
      setExportStatus(t("pygAnalitico.exportDoneBrands", { count }));
    } catch (e) {
      setExportStatus(e.message || t("pygAnalitico.errorExportBrand"));
    } finally {
      setExporting(false);
    }
  };

  const handleExportPorEmpresas = async () => {
    setExporting(true);
    setExportStatus(t("pygAnalitico.exportPreparingCompany"));
    try {
      const count = await exportPygPorEmpresas({
        apiUrl: API_URL,
        year,
        nivel1: nivel1Param,
        nivel2: nivel2Filter,
        t,
        onProgress: setExportStatus,
      });
      setExportStatus(t("pygAnalitico.exportDoneCompanies", { count }));
    } catch (e) {
      setExportStatus(e.message || t("pygAnalitico.errorExportCompany"));
    } finally {
      setExporting(false);
    }
  };

  const gantBg = theme.palette.gant?.main ?? theme.palette.gant ?? "#e0e0e0";
  const totalFinal = Number(groupMap?.["RESULTADO FINAL"]?.total || 0);
  const totalVentaReferencia = useMemo(
    () => Number(groupMap?.[PYG_TITULO_VENTA_NPGC]?.total ?? 0),
    [groupMap],
  );
  const vistaLabel = t(pygVistaLabelKey(activeVista), {
    defaultValue: vistaConfig.label,
  });

  const showHeroHeader = !compactEmbedded;

  const marcaChartLabel =
    nivel2Filter || (selectedMarca !== "Todas" ? selectedMarca : null);

  return (
    <Box
      sx={{
        ...(pageShell
          ? {
              display: "flex",
              flexDirection: "column",
              height: "100dvh",
              maxHeight: "100dvh",
              boxSizing: "border-box",
              overflow: "auto",
              WebkitOverflowScrolling: "touch",
              background: pageGradient,
              pt: { xs: 1, md: 1.25 },
              px: { xs: 1.25, sm: 2 },
              pr: { xs: 1.25, sm: 2, md: 3 },
              pb: { xs: 10, md: 1.5 },
              ...contentMargin,
              minWidth: 0,
              width: "100%",
            }
          : {
              p: embedded
                ? compactEmbedded
                  ? 0
                  : "0.5rem"
                : { xs: 1.25, sm: 2 },
              ...(embedded ? {} : contentMargin),
              width: "100%",
              boxSizing: "border-box",
              minWidth: 0,
            }),
      }}
    >
      {showHeroHeader && (
        <Box sx={{ flexShrink: 0 }}>
          <PygAnaliticoPageHeader
            sticky={pageShell ? !isMobile : !embedded}
            compact={pageShell}
            eyebrow={
              lockVista
                ? t("pygAnalitico.eyebrowNml")
                : t("pygAnalitico.eyebrowFinance")
            }
            title={t("pygAnalitico.titlePl", { year })}
            subtitle={
              nivel2Filter
                ? t("pygAnalitico.subtitleBrandDetail", { brand: nivel2Filter })
                : vistaLabel
            }
            filtroResumen={!embedded ? filtroResumen : undefined}
            filtroAside={
              showResultadoFinalChart && !embedded ? (
                <PygCompareYearsControl
                  compact
                  value={compareYears}
                  onChange={setCompareYears}
                  selectSx={pygToolbarFieldSx(theme)}
                  sx={{ width: "100%" }}
                />
              ) : null
            }
            exportStatus={!embedded ? exportStatus : undefined}
            kpiChips={
              <Stack direction="row" spacing={0.7} useFlexGap flexWrap="wrap">
                <Chip
                  size="small"
                  icon={<AutoGraphOutlinedIcon sx={{ fontSize: 16 }} />}
                  label={t("pygAnalitico.detail.year", { year })}
                  sx={{
                    fontWeight: 700,
                    bgcolor: theme.palette.background.paper,
                    border: "1px solid",
                    borderColor: theme.palette.divider,
                  }}
                />
                <Chip
                  size="small"
                  variant="outlined"
                  label={vistaLabel}
                  sx={{ fontWeight: 700 }}
                />
                {totalVentaReferencia !== 0 && (
                  <Chip
                    size="small"
                    label={`${t("pygAnalitico.sale")}: ${formatearNumero(totalVentaReferencia)}`}
                    sx={{
                      fontWeight: 700,
                      bgcolor: alpha(theme.palette.success.main, 0.1),
                      color: COLOR_VALOR_POSITIVO,
                      border: "1px solid",
                      borderColor: alpha(theme.palette.success.main, 0.3),
                    }}
                  />
                )}
                <Chip
                  size="small"
                  icon={
                    totalFinal >= 0 ? (
                      <TrendingUpOutlinedIcon sx={{ fontSize: 16 }} />
                    ) : (
                      <TrendingDownOutlinedIcon sx={{ fontSize: 16 }} />
                    )
                  }
                  label={`${t("pygAnalitico.result")}: ${formatearNumero(totalFinal)}`}
                  sx={{
                    fontWeight: 800,
                    bgcolor: theme.palette.background.paper,
                    color:
                      totalFinal >= 0
                        ? COLOR_VALOR_POSITIVO
                        : COLOR_VALOR_NEGATIVO,
                    border: "1px solid",
                    borderColor: alpha(theme.palette.divider, 0.95),
                  }}
                />
              </Stack>
            }
            navChips={
              !embedded && !nivel2 ? (
                lockVista ? (
                  <>
                    <Chip
                      size="small"
                      icon={<TableChartOutlinedIcon sx={{ fontSize: 16 }} />}
                      label={t("pygAnalitico.balance.navPyg")}
                      color="primary"
                      sx={{ fontWeight: 700 }}
                    />
                    <Chip
                      size="small"
                      icon={
                        <AccountBalanceOutlinedIcon sx={{ fontSize: 16 }} />
                      }
                      label={t("pygAnalitico.balance.navBalance")}
                      variant="outlined"
                      clickable
                      onClick={() => navigate(NML_BALANCE_PATH)}
                      sx={{ fontWeight: 700 }}
                    />
                  </>
                ) : (
                  <>
                    <Chip
                      size="small"
                      icon={<TableChartOutlinedIcon sx={{ fontSize: 16 }} />}
                      label={t("pygAnalitico.tableGlobal")}
                      color="primary"
                      sx={{ fontWeight: 700 }}
                    />
                    <Chip
                      size="small"
                      icon={<GridViewOutlinedIcon sx={{ fontSize: 16 }} />}
                      label={t("pygAnalitico.byBrand")}
                      variant="outlined"
                      clickable
                      onClick={() =>
                        navigate(pygAnaliticoBasePath(activeVista, true))
                      }
                      sx={{ fontWeight: 700 }}
                    />
                  </>
                )
              ) : null
            }
            toolbar={
              !embedded ? (
                <PygToolbarPanel variant="sidebar">
                  <PygToolbarFilters variant="sidebar">
                    {!lockVista && (
                      <FormControl
                        size="small"
                        fullWidth
                        sx={{
                          ...pygToolbarControlSx,
                          ...pygToolbarSidebarFullRowSx,
                        }}
                      >
                        <InputLabel>{t("pygAnalitico.view")}</InputLabel>
                        <Select
                          value={activeVista}
                          label={t("pygAnalitico.view")}
                          onChange={(e) =>
                            onVistaChange(String(e.target.value))
                          }
                          sx={pygToolbarFieldSx(theme)}
                        >
                          {PYG_VISTA_OPTIONS.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                              {t(pygVistaLabelKey(opt.value), {
                                defaultValue: opt.label,
                              })}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                    {!hideYearSelector && (
                      <FormControl
                        size="small"
                        fullWidth
                        sx={pygToolbarControlSx}
                      >
                        <InputLabel>{t("pygAnalitico.year")}</InputLabel>
                        <Select
                          value={year}
                          label={t("pygAnalitico.year")}
                          onChange={(e) => setYearState(Number(e.target.value))}
                          sx={pygToolbarFieldSx(theme)}
                        >
                          {[2023, 2024, 2025, 2026].map((y) => (
                            <MenuItem key={y} value={y}>
                              {y}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                    {!nivel2 && (
                      <FormControl
                        size="small"
                        fullWidth
                        disabled={loadingMarcas}
                        sx={pygToolbarControlSx}
                      >
                        <InputLabel>{t("pygAnalitico.brand")}</InputLabel>
                        <Select
                          value={selectedMarca}
                          label={t("pygAnalitico.brand")}
                          onChange={(e) =>
                            setSelectedMarca(String(e.target.value))
                          }
                          sx={pygToolbarFieldSx(theme)}
                        >
                          <MenuItem value="Todas">
                            {t("pygAnalitico.allBrands")}
                          </MenuItem>
                          {marcas.map((m) => (
                            <MenuItem key={m} value={m}>
                              {m}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  </PygToolbarFilters>
                  <PygExportMenu
                    disabled={exporting}
                    items={[
                      {
                        key: "vista",
                        label: t("pygAnalitico.exportCurrentView"),
                        onClick: exportToExcel,
                      },
                      ...(!nivel2 && !lockVista
                        ? [
                            {
                              key: "marcas",
                              label: t("pygAnalitico.exportByBrand"),
                              disabled: loadingMarcas || marcas.length === 0,
                              onClick: handleExportPorMarcas,
                            },
                            {
                              key: "empresas",
                              label: t("pygAnalitico.exportByCompany"),
                              onClick: handleExportPorEmpresas,
                            },
                          ]
                        : []),
                    ]}
                  />
                </PygToolbarPanel>
              ) : null
            }
          />
        </Box>
      )}

      <Box
        sx={
          pageShell
            ? {
                display: "flex",
                flexDirection: "column",
                gap: 1.25,
                flexShrink: 0,
              }
            : undefined
        }
      >
        {showResultadoFinalChart && (
          <Box
            sx={{
              mb: 1.25,
              flexShrink: 0,
              ...(pageShell
                ? {
                    ...pageContentCardSx(theme, accent),
                    p: { xs: 1.25, sm: 1.5 },
                  }
                : {}),
            }}
          >
            <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1.25 }}>
              {t("pygAnalitico.compareCharts")}
            </Typography>

            <Box sx={{ mb: 2 }}>
              <PygAnaliticoYearCompareChart
                variant="venta"
                compareYears={compareYears}
                serieByYear={ventaByYear}
                aramisSerieByYear={ventaContableByYear}
                loading={loadingVentaChart}
                error={errorVentaChart}
                formatearImporte={formatearNumero}
                vistaLabel={vistaLabel}
                marcaLabel={marcaChartLabel}
              />
              <PygAnaliticoPipelineVentaChart
                compareYears={compareYears}
                breakdownByYear={pipelineBreakdownByYear}
                loading={loadingPipelineChart}
                error={errorPipelineChart}
                formatearImporte={formatearNumero}
                vistaLabel={vistaLabel}
                marcaLabel={marcaChartLabel}
              />
            </Box>

            <PygAnaliticoYearCompareChart
              variant="resultado"
              compareYears={compareYears}
              serieByYear={resultadoFinalByYear}
              loading={loadingResultadoChart}
              error={errorResultadoChart}
              formatearImporte={formatearNumero}
              vistaLabel={vistaLabel}
              marcaLabel={marcaChartLabel}
            />
          </Box>
        )}

        {error && (
          <Typography color="error" sx={{ mb: 1, flexShrink: 0 }}>
            {error}
          </Typography>
        )}

        {loading && pageShell ? (
          <Box sx={{ p: 0.5 }}>
            <Skeleton variant="rounded" sx={{ minHeight: 280 }} />
          </Box>
        ) : loading ? (
          <Typography color="text.secondary" sx={{ mb: 1 }}>
            {t("pygAnalitico.loading")}
          </Typography>
        ) : null}

        {(!loading || !pageShell) && (
          <TableContainer
            component={Paper}
            sx={{
              marginTop: pageShell ? 0 : 1,
              overflowX: "auto",
              ...(pageShell
                ? {
                    ...pageContentCardSx(theme, accent),
                    p: 0,
                  }
                : {
                    maxHeight: compactEmbedded ? "55vh" : "87vh",
                    overflowY: "auto",
                    borderRadius: 3,
                    border: "1px solid",
                    borderColor: theme.palette.divider,
                    boxShadow:
                      theme.palette.mode === "dark"
                        ? "0 10px 24px rgba(0,0,0,0.34)"
                        : "0 10px 24px rgba(15,23,42,0.08)",
                    backgroundColor: theme.palette.background.paper,
                  }),
            }}
          >
            <Table
              size="small"
              sx={{
                "& .MuiTableCell-root": {
                  py: 0.65,
                  borderBottomColor: alpha(theme.palette.divider, 0.6),
                },
                "& .MuiTableBody-root .MuiTableRow-root:nth-of-type(odd)": {
                  backgroundColor: alpha(
                    theme.palette.background.default,
                    0.28,
                  ),
                },
                "& .MuiTableBody-root .MuiTableRow-root:hover": {
                  backgroundColor: alpha(theme.palette.primary.main, 0.06),
                },
                "& .MuiTableCell-root:first-of-type": {
                  boxShadow: `2px 0 0 ${alpha(theme.palette.divider, 0.55)}`,
                },
              }}
            >
              <TableHead
                sx={{
                  borderLeft: `1px solid ${theme.palette.pyg || "#444"}`,
                  position: "sticky",
                  top: 0,
                  backgroundColor: theme.palette.background.paper,
                  zIndex: 2,
                  "& .MuiTableCell-root": {
                    fontWeight: 800,
                    letterSpacing: 0.25,
                  },
                }}
              >
                <TableRow>
                  <TableCell
                    sx={{
                      position: "sticky",
                      top: 0,
                      left: 0,
                      backgroundColor: theme.palette.background.paper,
                      zIndex: 1,
                      paddingLeft: "1em",
                    }}
                  >
                    <strong>{t("pygAnalitico.titleCol")}</strong>
                  </TableCell>
                  {meses.map((mes) => (
                    <TableCell
                      key={mes}
                      align="right"
                      sx={{
                        position: "sticky",
                        top: 0,
                        backgroundColor: theme.palette.background.paper,
                        zIndex: 1,
                        borderLeft: `1px solid ${theme.palette.pyg || "#444"}`,
                      }}
                    >
                      <strong>{mes}</strong>
                    </TableCell>
                  ))}
                  <TableCell
                    align="right"
                    sx={{
                      position: "sticky",
                      top: 0,
                      backgroundColor: theme.palette.background.paper,
                      zIndex: 1,
                      borderLeft: `1px solid ${theme.palette.pyg || "#444"}`,
                    }}
                  >
                    <strong>{t("pygAnalitico.total")}</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groupOrder.map((titulo) => {
                  const g = groupMap[titulo];
                  const isCalculated = g?._calculated;
                  const hasDetalle = hasPygSerieDetalle(titulo);
                  const open = isCalculated ? false : gruposAbiertos[titulo];
                  const rowBg = isCalculated
                    ? (theme.palette.pyg1?.main ??
                      theme.palette.pyg1 ??
                      "#d4e6e0")
                    : gantBg;
                  return (
                    <React.Fragment key={titulo}>
                      <TableRow
                        sx={{
                          backgroundColor: rowBg,
                          cursor: isCalculated ? "default" : "pointer",
                        }}
                        onClick={() => !isCalculated && toggleGrupo(titulo)}
                      >
                        <TableCell
                          sx={{
                            position: "sticky",
                            left: 0,
                            backgroundColor: rowBg,
                            fontWeight: "bold",
                            fontSize: "12px",
                            paddingLeft: "1em",
                          }}
                        >
                          {isCalculated ? "" : open ? "▼ " : "► "}
                          {translatePygLine(t, titulo)}
                          {hasDetalle ? (
                            <Tooltip title={t("pygAnalitico.seeLineDetail")}>
                              <IconButton
                                size="small"
                                aria-label={t("pygAnalitico.seeDetailOf", {
                                  title: translatePygLine(t, titulo),
                                })}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openSerieDetalle(titulo, null, g.total);
                                }}
                                sx={{ ml: 0.5, verticalAlign: "middle" }}
                              >
                                <ListAltOutlinedIcon fontSize="inherit" />
                              </IconButton>
                            </Tooltip>
                          ) : null}
                        </TableCell>
                        {meses.map((mes) => {
                          const numMes = parseInt(mes, 10);
                          const val = g.mes[numMes] ?? g.mes[mes];
                          return (
                            <TableCell
                              key={mes}
                              align="right"
                              onClick={
                                hasDetalle
                                  ? (e) => {
                                      e.stopPropagation();
                                      openSerieDetalle(titulo, numMes, val);
                                    }
                                  : undefined
                              }
                              sx={{
                                ...getEstiloValor(val),
                                borderLeft: `1px solid ${theme.palette.pyg || "#444"}`,
                                ...(hasDetalle
                                  ? {
                                      cursor: "pointer",
                                      "&:hover": {
                                        backgroundColor: alpha(
                                          theme.palette.primary.main,
                                          0.1,
                                        ),
                                        textDecoration: "underline dotted",
                                      },
                                    }
                                  : {}),
                              }}
                            >
                              {formatearNumero(val)}
                            </TableCell>
                          );
                        })}
                        <CeldaTotalConPctVenta
                          total={g.total}
                          ventaReferencia={totalVentaReferencia}
                          onClick={
                            hasDetalle
                              ? (e) => {
                                  e.stopPropagation();
                                  openSerieDetalle(titulo, null, g.total);
                                }
                              : undefined
                          }
                          sx={{
                            borderLeft: `1px solid ${theme.palette.pyg || "#444"}`,
                            ...(hasDetalle
                              ? {
                                  cursor: "pointer",
                                  "&:hover": {
                                    backgroundColor: alpha(
                                      theme.palette.primary.main,
                                      0.1,
                                    ),
                                  },
                                }
                              : {}),
                          }}
                        />
                      </TableRow>
                      {open &&
                        (g.subGroupOrder || []).map((subKey) => {
                          const sub = g.subGroups[subKey];
                          if (!sub) return null;
                          return (
                            <React.Fragment key={subKey}>
                              {/* Fila subgrupo (ej. "a) Consumo de mercaderías") */}
                              {sub.title && (
                                <TableRow
                                  sx={{
                                    backgroundColor:
                                      theme.palette.background.default ??
                                      "#f5f5f5",
                                  }}
                                >
                                  <TableCell
                                    sx={{
                                      paddingLeft: "2em",
                                      position: "sticky",
                                      left: 0,
                                      backgroundColor:
                                        theme.palette.background.default ??
                                        "#f5f5f5",
                                      fontWeight: "bold",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {translatePygLine(t, sub.title)}
                                  </TableCell>
                                  {meses.map((mes) => {
                                    const numMes = parseInt(mes, 10);
                                    const val = sub.mes[numMes] ?? sub.mes[mes];
                                    return (
                                      <TableCell
                                        key={mes}
                                        align="right"
                                        sx={{
                                          ...getEstiloValor(val),
                                          borderLeft: `1px solid ${theme.palette.pyg || "#eee"}`,
                                        }}
                                      >
                                        {formatearNumero(val)}
                                      </TableCell>
                                    );
                                  })}
                                  <CeldaTotalConPctVenta
                                    total={sub.total}
                                    ventaReferencia={totalVentaReferencia}
                                    sx={{
                                      borderLeft: `1px solid ${theme.palette.pyg || "#444"}`,
                                    }}
                                  />
                                </TableRow>
                              )}
                              {/* Cuentas del subgrupo */}
                              {(sub.accountList || []).map((acc) => (
                                <TableRow key={acc.codigo}>
                                  <TableCell
                                    sx={{
                                      paddingLeft: "3.25em",
                                      position: "sticky",
                                      left: 0,
                                      backgroundColor:
                                        theme.palette.background.paper,
                                      fontSize: "12px",
                                    }}
                                  >
                                    {acc.titulo}
                                  </TableCell>
                                  {meses.map((mes) => {
                                    const numMes = parseInt(mes, 10);
                                    const val = acc.mes[numMes] ?? acc.mes[mes];
                                    return (
                                      <TableCell
                                        key={mes}
                                        align="right"
                                        sx={{
                                          ...getEstiloValor(val),
                                          borderLeft: `1px solid ${theme.palette.pyg || "#eee"}`,
                                        }}
                                      >
                                        {formatearNumero(val)}
                                      </TableCell>
                                    );
                                  })}
                                  <CeldaTotalConPctVenta
                                    total={acc.total}
                                    ventaReferencia={totalVentaReferencia}
                                    fontWeight={500}
                                    sx={{
                                      borderLeft: `1px solid ${theme.palette.pyg || "#444"}`,
                                    }}
                                  />
                                </TableRow>
                              ))}
                            </React.Fragment>
                          );
                        })}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <PygSerieDetalleDialog
          open={Boolean(detalleDialog)}
          onClose={closeSerieDetalle}
          titulo={detalleDialog?.titulo}
          year={year}
          empresa={empresaApi}
          nivel1={nivel1Api}
          omitirGeneralStage={omitirGeneralStage}
          nivel2={nivel2Filter}
          mes={detalleDialog?.mes ?? null}
          vistaLabel={vistaLabel}
          totalEsperado={detalleDialog?.totalEsperado ?? null}
        />
      </Box>
    </Box>
  );
};

export default PygAnaliticoTable;
